'use client';

import { Menu as MenuType } from './types';
import MenuLink from '../link/MenuLink';
import { LiveMenu } from './LiveMenu';
import { FindMenu } from './FindMenu';
import { SearchMenu } from './SearchMenu';
import { useEffect, useRef } from 'react';
import { WarnMenu } from './WarnMenu';

interface Props {
  position?: 'left' | 'right';
  menus: Array<MenuType>;
  styles?: string;
  type?: 'top' | 'main' | 'search';
  hoverMainMenuEffect?: boolean;
  hoverTopMenuEffect?: boolean;
  isSticky?: boolean;
  topHeader?: boolean;
  topRightHeader?: boolean;
}

export const MenuFC = ({
  position,
  menuItems,
  type,
  onClick,
}: {
  position?: 'left' | 'right';
  menuItems: Array<MenuType>;
  type: 'top' | 'main' | 'search';
  onClick?: () => void;
}) => {
  const subMenuComponent = (menuItem: MenuType) => (
    <MenuFC
      menuItems={menuItem.children || []}
      type={type}
      onClick={() => {
        (window.document?.activeElement as HTMLElement).blur();
        if (onClick) {
          onClick();
        }
      }}
    />
  );

  return (
    <>
      {menuItems.map((menuItem, index) => {
        return (
          <li key={index} className="capitalizeFirstLetter">
            <MenuLink
              {...menuItem}
              prefetch={false}
              type={type}
              styles={`text-sm md:text-sm font-Manrope font-bold ${position === 'left' ? 'text-white' : 'inherit'} ${menuItem.children.length > 0 ? 'text-slate-400' : 'capitalizeFirstLetter'}`}
              onClick={() => {
                (window.document?.activeElement as HTMLElement).blur();
                if (onClick) {
                  onClick();
                }
              }}
              hoverSubMenuEffect={menuItems.length > 0}
            />
            {subMenuComponent && <ul>{subMenuComponent(menuItem)}</ul>}
          </li>
        );
      })}
    </>
  );
};

const MenuWithRef = ({
  position,
  menu,
  hoverMainMenuEffect,
  hoverTopMenuEffect,
  hoverEffectClass,
  isSticky,
  styles,
  type,
  topHeader,
}: {
  position?: 'left' | 'right';
  menu: MenuType;
  hoverMainMenuEffect: boolean;
  hoverTopMenuEffect: boolean;
  hoverEffectClass: string;
  isSticky: boolean;
  styles: string;
  type: 'top' | 'main' | 'search';
  topHeader?: boolean;
}) => {
  const menuLinkRef = useRef<HTMLAnchorElement>(null);
  const ulRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    const menuLink = menuLinkRef.current;
    const ul = ulRef.current;

    if (ul) {
      ul.addEventListener('mouseover', () => {
        if (!topHeader) {
          menuLink?.classList.add('text-slate-950');
        }
      });

      ul.addEventListener('mouseout', () => {
        if (!topHeader) {
          menuLink?.classList.remove('text-slate-950');
        }
      });
    }

    return () => {
      if (ul) {
        ul.removeEventListener('mouseover', () => {
          if (!topHeader) {
            menuLink?.classList.add('text-slate-950');
          }
        });

        ul.removeEventListener('mouseout', () => {
          if (!topHeader) {
            menuLink?.classList.remove('text-slate-950');
          }
        });
      }
    };
  }, [topHeader]);

  const menuTopPosition = `${topHeader ? 'top-[45px]' : isSticky ? 'top-[75px]' : 'top-[97px]'} `;
  const menuHeight = `${topHeader ? '' : isSticky ? 'min-h-[75px]' : 'min-h-[95px]'}`;

  return (
    <div
      className={`dropdown dropdown-hover flex h-full items-center ${hoverEffectClass} ${menuHeight}`}
      key={menu.label}
    >
      <div className={`inline-flex h-full items-center rounded-lg ${styles} `}>
        <MenuLink
          {...menu}
          type={type}
          hoverMainMenuEffect={hoverMainMenuEffect}
          hoverTopMenuEffect={hoverTopMenuEffect}
          styles={`flex items-center h-full uppercase`}
          ref={menuLinkRef}
          prefetch={false}
          onClick={() => {
            (window.document?.activeElement as HTMLElement).blur();
          }}
        />
        {menu?.children?.length > 0 && (
          <ul
            ref={ulRef}
            className={`menu dropdown-content ${menuTopPosition} left-1/2 z-40 min-w-max -translate-x-1/2 transform overflow-hidden rounded-b-lg rounded-t-lg border-l border-r border-t border-gray-100 ${position === 'left' ? 'bg-ffbb-top-header-bg' : 'bg-white'} px-3.5 py-5 text-black opacity-0 transition-opacity duration-1000 ease-in-out menu-gradient`}
          >
            <MenuFC menuItems={menu.children} type={type} position={position} />
          </ul>
        )}
      </div>
    </div>
  );
};

export default function Menu({
  position,
  menus,
  styles = '',
  type = 'top',
  hoverMainMenuEffect = false,
  hoverTopMenuEffect = false,
  isSticky = false,
  topHeader = false,
  topRightHeader = false,
}: Props) {
  const hoverEffectClass = hoverMainMenuEffect
    ? isSticky
      ? 'hover:menu-active-sticky'
      : 'hover:menu-active'
    : '';
  let height = type === 'main' ? 'h-[75px]' : 'h-[45px]';
  if (type === 'search') {
    height = 'h-[50px]';
  }

  let gapX = topRightHeader ? '' : 'gap-x-5';

  if (type === 'search') {
    gapX = 'gap-[10px]';
  }

  return (
    <div
      className={`flex group ${height} items-center justify-center ${gapX} ${type === 'top' ? '' : 'hover:text-gray-400'}`}
    >
      {menus.map((menu, index) => {
        const menuLabel = menu.label.toLowerCase();
        if (menuLabel === 'live') {
          return (
            <LiveMenu key={menu.label} styles="flex items-center h-full text-xs" menu={menu} />
          );
        }
        if (menuLabel === 'trouver') {
          return (
            <FindMenu key={menu.label} styles="flex items-center h-full text-xs" menu={menu} />
          );
        }
        if (menuLabel === 'recherche') {
          return (
            <SearchMenu
              key={menu.label}
              styles="flex items-center w-[153px] text-xs hover:text-current"
            />
          );
        }
        if (menuLabel.includes('signale')) {
          return (
            <WarnMenu
              key={menu.label}
              menu={menu}
              styles="flex items-center h-full text-xs hover:text-current"
            />
          );
        }

        return (
          <MenuWithRef
            key={index}
            menu={menu}
            hoverMainMenuEffect={hoverMainMenuEffect}
            hoverEffectClass={hoverEffectClass}
            hoverTopMenuEffect={hoverTopMenuEffect}
            isSticky={isSticky}
            styles={styles}
            type={type}
            position={position}
            topHeader={topHeader}
          />
        );
      })}
    </div>
  );
}
